import React from 'react';
import { Property, AppUser } from '../../types';
import { XMarkIcon } from '../Icons';
import { VisitForm } from './visit/VisitForm';
import { VisitConfirmation } from './visit/VisitConfirmation';
import { VisitStatus } from './visit/VisitStatus';
import { VisitPurposeSelector } from './visit/VisitPurposeSelector';
import { useScheduleVisitLogic } from './visit/useScheduleVisitLogic';

interface ScheduleVisitModalProps {
  isOpen: boolean;
  onClose: () => void;
  property: Property;
  appUser: AppUser | null;
}

export const ScheduleVisitModal: React.FC<ScheduleVisitModalProps> = ({
  isOpen,
  onClose,
  property,
  appUser: initialAppUser,
}) => {
  const {
    status,
    setStatus,
    visitType,
    setVisitType,
    handleSelectVisitType,
    formData,
    setFormData,
    visitConfig,
    errorData,
    minDate,
    handleSubmit,
    isBrokerProfile,
    isClientView,
  } = useScheduleVisitLogic(isOpen, property, initialAppUser);

  if (!isOpen) return null;

  const isSelectingPurpose = isBrokerProfile && visitType === null;

  return (
    <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm z-[999] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-w-2xl w-full max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden animate-fade-in-up">
        {!isSelectingPurpose && (
          <header className="px-6 py-4 sm:px-8 sm:py-6 border-b border-gray-100 flex justify-between items-start flex-shrink-0">
            <div>
              <h3 className="text-xl sm:text-2xl font-black text-[#00234F] uppercase tracking-tight">
                Agendar Visita
              </h3>
              <p className="text-xs text-sky-600 font-bold mt-0.5 sm:mt-1">
                Inmueble: {property.codigo}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-red-500 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </header>
        )}

        {['fetching_config', 'loading', 'success', 'error'].includes(status) ? (
          <VisitStatus
            status={status as any}
            errorData={errorData}
            propertyCode={property.codigo}
            onClose={onClose}
            onRetry={() => setStatus('form')}
          />
        ) : status === 'confirmation' ? (
          <VisitConfirmation
            formData={formData}
            onConfirm={handleSubmit}
            onBack={() => setStatus('form')}
            isClientView={isClientView}
            isReconocimiento={visitType === 'reconocimiento'}
          />
        ) : isSelectingPurpose ? (
          <VisitPurposeSelector
            property={property}
            onSelect={handleSelectVisitType}
            onClose={onClose}
          />
        ) : (
          <VisitForm
            formData={formData}
            setFormData={setFormData}
            visitConfig={visitConfig}
            minDate={minDate}
            isBrokerProfile={isBrokerProfile}
            isClientView={isClientView}
            isReconocimiento={visitType === 'reconocimiento'}
            onNext={() => setStatus('confirmation')}
            onCancel={isBrokerProfile ? () => setVisitType(null) : onClose}
          />
        )}
      </div>
    </div>
  );
};
